import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import PartCard from "../components/PartCard";
import { Loader2, SlidersHorizontal, X } from "lucide-react";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, items: [] });
  const [categories, setCategories] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const make = params.get("make") || "";
  const model = params.get("model") || "";
  const year = params.get("year") || "";
  const sort = params.get("sort") || "relevance";
  const verifiedOnly = params.get("verified") === "1";
  const vin = params.get("vin");

  useEffect(() => {
    api.get("/catalog/categories").then(r => setCategories(r.data));
    api.get("/catalog/makes").then(r => setMakes(r.data));
  }, []);

  useEffect(() => {
    if (make) {
      api.get("/catalog/models", { params: { make } }).then(r => setModels(r.data));
    } else {
      setModels([]);
    }
  }, [make]);

  useEffect(() => {
    setLoading(true);
    const p = { limit: 36, sort };
    if (q) p.q = q;
    if (category) p.category = category;
    if (make) p.make = make;
    if (model) p.model = model;
    if (year) p.year = year;
    if (verifiedOnly) p.verified_only = true;
    api.get("/parts", { params: p })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [q, category, make, model, year, sort, verifiedOnly]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === "make") next.delete("model");
    setParams(next);
  };

  const clear = () => setParams(new URLSearchParams());

  const years = Array.from({ length: 25 }, (_, i) => 2025 - i);

  return (
    <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="search-page">
      {vin && (
        <div className="bg-[#FFD100]/30 border-l-4 border-[#FFD100] px-4 py-3 mb-6 text-sm">
          <span className="font-bold uppercase tracking-wider text-xs mr-2">VIN:</span>
          <span className="font-mono">{vin}</span>
        </div>
      )}

      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="search-heading">
            {category || "All parts"}
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            {loading ? "Loading…" : `${data.total} parts available`}
            {make && <> · compatible with <span className="font-semibold text-[#121212]">{make}{model ? ` ${model}` : ""}{year ? ` ${year}` : ""}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="lg:hidden btn-outline-dark !py-2 !px-4 text-xs"
            onClick={() => setShowFilters(v => !v)}
            data-testid="toggle-filters"
          >
            <SlidersHorizontal size={14} /> Filters
          </button>
          <select
            value={sort}
            onChange={(e) => update("sort", e.target.value)}
            className="input-apa !py-2 !text-sm"
            data-testid="sort-select"
          >
            <option value="relevance">Most relevant</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
            <option value="rating">Top rated</option>
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters */}
        <aside className={`${showFilters ? "block" : "hidden"} lg:block bg-white border border-[#E4E4E7] p-5 h-fit sticky top-24`} data-testid="filters-panel">
          <div className="flex items-center justify-between mb-5">
            <div className="font-display font-bold uppercase tracking-widest text-xs">Filters</div>
            <button onClick={clear} className="text-xs text-[#EF3340] hover:underline" data-testid="clear-filters">
              Clear all
            </button>
          </div>
          <div className="space-y-5">
            <FilterGroup label="Search">
              <input
                value={q}
                onChange={(e) => update("q", e.target.value)}
                placeholder="Part name, number…"
                className="input-apa !text-sm !py-2"
                data-testid="filter-q"
              />
            </FilterGroup>
            <FilterGroup label="Category">
              <select value={category} onChange={(e) => update("category", e.target.value)} className="input-apa !py-2 !text-sm" data-testid="filter-category">
                <option value="">All</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Make">
              <select value={make} onChange={(e) => update("make", e.target.value)} className="input-apa !py-2 !text-sm" data-testid="filter-make">
                <option value="">Any</option>
                {makes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Model">
              <select value={model} onChange={(e) => update("model", e.target.value)} className="input-apa !py-2 !text-sm" disabled={!make} data-testid="filter-model">
                <option value="">Any</option>
                {models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Year">
              <select value={year} onChange={(e) => update("year", e.target.value)} className="input-apa !py-2 !text-sm" data-testid="filter-year">
                <option value="">Any</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </FilterGroup>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={(e) => update("verified", e.target.checked ? "1" : "")}
                className="w-4 h-4 accent-[#009A44]"
                data-testid="filter-verified-only"
              />
              <span>Agent verified only</span>
            </label>
          </div>
        </aside>

        {/* Results */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-32"><Loader2 size={28} className="animate-spin text-[#009A44]" /></div>
          ) : data.items.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] p-12 text-center" data-testid="no-results">
              <div className="font-display text-2xl font-bold mb-2">No parts match your filters</div>
              <p className="text-zinc-500 mb-4">Try broadening your search or removing some filters.</p>
              <button onClick={clear} className="btn-outline-dark">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="search-results-grid">
              {data.items.map(p => <PartCard key={p.part_id} part={p} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2">{label}</div>
      {children}
    </div>
  );
}
